import React, { useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { addNewDeviceFamily } from 'api/device_family';
import { Button } from './ui/Button';
import { colors } from '../theme/colors';

interface EntrySchema {
  point_name: string;
  shown_name: string
  measured_values: string;
  description: string;
}

interface DeviceFamily {
  device_name: string;
  data_schema: EntrySchema[];
  description: string;
}

interface AddDeviceFamilyFormProps {
  onSuccess: () => void;
}

const DeviceFamilyForm: React.FC<AddDeviceFamilyFormProps> = ({ onSuccess }) => {
  const [deviceName, setDeviceName] = useState<string>('');
  const [deviceDescription, setDeviceDescription] = useState<string>('');
  const [entries, setEntries] = useState<EntrySchema[]>([
    { point_name: '', measured_values: '', description: '', shown_name: '' },
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddEntry = () => {
    setEntries([...entries, { point_name: '', measured_values: '', description: '', shown_name: '' }]);
  };

  const handleRemoveEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  const handleChangeEntry = (index: number, field: keyof EntrySchema, value: string) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    setEntries(newEntries);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      setIsLoading(true);
      const deviceFamily: DeviceFamily = {
        device_name: deviceName,
        description: deviceDescription,
        data_schema: entries,
      };
      const result = await addNewDeviceFamily(deviceFamily);

      if (result) {
        setSuccess(true);
        // Reset form after short delay
        setTimeout(() => {
          setDeviceName('');
          setDeviceDescription('');
          setEntries([{ point_name: '', measured_values: '', description: '', shown_name: '' }]);
          setSuccess(false);
          onSuccess();
        }, 1500);
      } else {
        setError('Не удалось добавить семейство устройств.');
      }
    } catch (err) {
      setError('Произошла ошибка. Попробуйте снова.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: colors.text.primary,
        }}
      >
        Добавить новое семейство устройств
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            mb: 3,
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(-10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          Семейство устройств успешно добавлено!
        </Alert>
      )}

      <TextField
        label="Имя семейства"
        fullWidth
        value={deviceName}
        onChange={(e) => setDeviceName(e.target.value)}
        required
        disabled={isLoading || success}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Описание"
        fullWidth
        multiline
        rows={2}
        value={deviceDescription}
        onChange={(e) => setDeviceDescription(e.target.value)}
        required
        disabled={isLoading || success}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 3 }}>
        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
          Схема данных
        </Typography>
      </Divider>

      {entries.map((entry, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: `1px solid ${colors.neutral[200]}`,
            borderRadius: 2,
            backgroundColor: colors.neutral[50],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <TextField
              label="Программное имя"
              value={entry.point_name}
              onChange={(e) => handleChangeEntry(index, 'point_name', e.target.value)}
              required
              disabled={isLoading || success}
              size="small"
              sx={{ flex: 1 }}
              placeholder="temperature"
            />
            <TextField
              label="Отображаемое имя"
              value={entry.shown_name}
              onChange={(e) => handleChangeEntry(index, 'shown_name', e.target.value)}
              required
              disabled={isLoading || success}
              size="small"
              sx={{ flex: 1 }}
              placeholder="Температура"
            />
            <TextField
              label="Измеряемые значения"
              value={entry.measured_values}
              onChange={(e) => handleChangeEntry(index, 'measured_values', e.target.value)}
              required
              disabled={isLoading || success}
              size="small"
              sx={{ flex: 1 }}
              placeholder="celsius"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Описание"
              value={entry.description}
              onChange={(e) => handleChangeEntry(index, 'description', e.target.value)}
              required
              disabled={isLoading || success}
              size="small"
              fullWidth
              placeholder="Температура окружающей среды"
            />
            <IconButton
              onClick={() => handleRemoveEntry(index)}
              disabled={entries.length === 1 || isLoading || success}
              sx={{
                color: colors.error.main,
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                },
              }}
            >
              <Remove />
            </IconButton>
          </Box>
        </Paper>
      ))}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="ghost"
          onClick={handleAddEntry}
          startIcon={<Add />}
          disabled={isLoading || success}
        >
          Добавить поле
        </Button>
      </Box>

      <Button type="submit" variant="gradient" fullWidth disabled={isLoading || success} loading={isLoading}>
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
            Добавление...
          </>
        ) : (
          'Добавить семейство устройств'
        )}
      </Button>
    </Box>
  );
};

export default DeviceFamilyForm;